<template>
  <a-layout-content class="wemail-sites-overview">
    <a-breadcrumb :style="{ marginBottom: '20px' }">
      <a-breadcrumb-item>
        <router-link tag="a" :to="{ name: 'sites.overview', params: { siteId: this.$route.params.siteId } }">Site</router-link>
      </a-breadcrumb-item>
      <a-breadcrumb-item>Site Team</a-breadcrumb-item>
    </a-breadcrumb>
    <div class="wemail-tabs-wrap">
      <SitesNavigation/>
      <div class="wemail-overview-content">
        <a-card title="Team members">
          <a-button slot="extra" type="primary" icon="plus" @click="showAddModal">Add member</a-button>

          <a-table
            :columns="columns"
            :dataSource="members"
            :loading="loading"
            :rowKey="record => record.id"
            :pagination="false"
          >
            <span slot="name" slot-scope="name, record">
              {{ name || '—' }}
              <a-tag v-if="record.is_owner" color="gold" style="margin-left: 8px">Owner</a-tag>
            </span>
            <span slot="email" slot-scope="email">
              <a :href="`mailto:${email}`">{{ email }}</a>
            </span>
            <span slot="role" slot-scope="role, record">
              <span v-if="record.is_owner">{{ record.role_title }}</span>
              <a-select
                v-else
                :value="role"
                :disabled="savingId === record.id"
                style="width: 120px"
                @change="value => updateMember(record, { role: value })"
              >
                <a-select-option v-for="option in roles" :key="option.value" :value="option.value">
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </span>
            <span slot="active" slot-scope="active, record">
              <a-switch
                v-if="!record.is_owner"
                :checked="active"
                :loading="savingId === record.id"
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                @change="value => updateMember(record, { status: value })"
              />
              <a-tag v-else color="#87d068">Active</a-tag>
            </span>
            <span slot="created_at" slot-scope="createdAt">{{ formatDate(createdAt) }}</span>
            <span slot="action" slot-scope="record">
              <a-popconfirm
                v-if="!record.is_owner"
                title="Remove this member from the site?"
                okText="Remove"
                cancelText="Cancel"
                @confirm="removeMember(record)"
              >
                <a-button type="danger" size="small" :disabled="savingId === record.id">Remove</a-button>
              </a-popconfirm>
            </span>
          </a-table>
        </a-card>
      </div>
    </div>

    <a-modal
      title="Add team member"
      v-model="addModalVisible"
      okText="Add"
      :confirmLoading="adding"
      @ok="addMember"
    >
      <p>The email must belong to an existing weMail account.</p>
      <a-form layout="vertical" @submit.prevent="addMember">
        <a-form-item
          label="Email"
          :validateStatus="addErrors.email ? 'error' : ''"
          :help="addErrors.email"
        >
          <a-input v-model.trim="newMember.email" type="email" placeholder="user@example.com"/>
        </a-form-item>
        <a-form-item
          label="Role"
          :validateStatus="addErrors.role ? 'error' : ''"
          :help="addErrors.role"
        >
          <a-select v-model="newMember.role">
            <a-select-option v-for="option in roles" :key="option.value" :value="option.value">
              {{ option.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </a-layout-content>
</template>

<script>
import moment from "moment";
import SitesNavigation from "@/components/SitesNavigation.vue";
import Api from "@/services/Api";

const columns = [
  { title: "Name", dataIndex: "name", scopedSlots: { customRender: "name" } },
  { title: "Email", dataIndex: "email", scopedSlots: { customRender: "email" } },
  { title: "Role", dataIndex: "role", scopedSlots: { customRender: "role" } },
  { title: "Status", dataIndex: "active", scopedSlots: { customRender: "active" } },
  { title: "Added", dataIndex: "created_at", scopedSlots: { customRender: "created_at" } },
  { title: "Action", key: "action", scopedSlots: { customRender: "action" } }
];

export default {
  components: { SitesNavigation },

  data() {
    return {
      columns,
      roles: [
        { value: "admin", label: "Admin" },
        { value: "team", label: "Team" }
      ],
      members: [],
      loading: true,
      savingId: null,
      addModalVisible: false,
      adding: false,
      newMember: { email: "", role: "team" },
      addErrors: {}
    };
  },

  computed: {
    endpoint() {
      return `sites/${this.$route.params.siteId}/team`;
    }
  },

  methods: {
    loadMembers() {
      this.loading = true;
      Api.get(this.endpoint)
        .then(res => {
          this.members = res.data.data;
        })
        .catch(err => this.$weNotify("error", this.errorMessage(err)))
        .finally(() => {
          this.loading = false;
        });
    },

    showAddModal() {
      this.newMember = { email: "", role: "team" };
      this.addErrors = {};
      this.addModalVisible = true;
    },

    addMember() {
      this.adding = true;
      this.addErrors = {};
      Api.post(this.endpoint, this.newMember)
        .then(() => {
          this.addModalVisible = false;
          this.$weNotify("success", "Team member added.");
          this.loadMembers();
        })
        .catch(err => {
          const errors = (err.response && err.response.data.errors) || {};
          this.addErrors = {
            email: errors.email ? errors.email[0] : "",
            role: errors.role ? errors.role[0] : ""
          };
          if (!errors.email && !errors.role) {
            this.$weNotify("error", this.errorMessage(err));
          }
        })
        .finally(() => {
          this.adding = false;
        });
    },

    updateMember(record, changes) {
      this.savingId = record.id;
      Api.patch(`${this.endpoint}/${record.id}`, changes)
        .then(res => {
          const index = this.members.findIndex(member => member.id === record.id);
          this.members.splice(index, 1, res.data.data);
          this.$weNotify("success", "Team member updated.");
        })
        .catch(err => this.$weNotify("error", this.errorMessage(err)))
        .finally(() => {
          this.savingId = null;
        });
    },

    removeMember(record) {
      this.savingId = record.id;
      Api.delete(`${this.endpoint}/${record.id}`)
        .then(() => {
          this.members = this.members.filter(member => member.id !== record.id);
          this.$weNotify("success", "Team member removed.");
        })
        .catch(err => this.$weNotify("error", this.errorMessage(err)))
        .finally(() => {
          this.savingId = null;
        });
    },

    formatDate(value) {
      return value ? moment(value).format("MMM D, YYYY") : "—";
    },

    errorMessage(err) {
      const data = err.response && err.response.data;
      if (data && data.message) {
        return data.message;
      }
      if (data && data.errors && data.errors.root) {
        return data.errors.root;
      }
      return "Something went wrong.";
    }
  },

  mounted() {
    this.loadMembers();
  }
};
</script>
